import { Controller } from 'stimulus';
import match from '../helpers/match';
import pathNode from '../helpers/connector/pathNode';
import ConnectorHorizontal from '../helpers/connector/connectorHorizontal';
import ConnectorReview from '../helpers/connector/connectorReview';
import ConnectorProcess from '../helpers/connector/connectorProcess';
import ConnectorSocials from '../helpers/connector/connectorSocials';
import ConnectorApply from '../helpers/connector/connectorApply';
import svgNode from '../helpers/connector/svgNode';
import truckGroupNode from '../helpers/connector/truckGroupNode';

class Connector extends Controller {
  connect() {
    // Calculate options like scroll positions, sizes, etc.
    this.recalculateOptions();

    const blocks = this.blockTargets;

    if (blocks.length < 2) return;

    this.element.style.position = 'relative';

    // Create SVG element that overlaps the whole element
    this.svg = svgNode();
    this.element.appendChild(this.svg);

    // Blocks that can not be directly linked, are separated by
    // spacer elements. These should get a height.
    this.setConnectorSpacerHeights();

    // Draw paths between blocks
    this.path = this.drawConnections(this.svg);
    this.drawTruck(this.svg);
    this.updateTruckPosition();

    window.addEventListener('connector:recalculate', this.onRecalculate.bind(this));
  }

  setConnectorSpacerHeights() {
    this.blockTargets.forEach((block) => {
      if (!block.classList.contains('connector-spacer')) return;

      block.style.height = this.isMobile ? 'auto' : `${this.curveSize * 2}px`;
    });
  }

  recalculateOptions() {
    this.curveSize = 40;
    this.isMobile = window.innerWidth < 992;
    this.strokeWidth = this.isMobile ? 16 : 22;

    const elementTopY = this.element.getBoundingClientRect().top + window.scrollY;

    const startY = elementTopY - window.innerHeight;
    const endY = elementTopY + this.element.offsetHeight;

    this.startY = startY;
    this.endY = endY;

    this.endY += window.innerHeight;

    this.previousScrollY = window.scrollY;
    this.scrollY = window.scrollY;
  }

  onRecalculate() {
    this.onWindowResize();
  }

  onWindowResize() {
    this.fullRedraw();
  }

  onWindowLoad() {
    this.fullRedraw();
  }

  onWindowScroll() {
    this.previousScrollY = this.scrollY;
    this.scrollY = window.scrollY;
    this.updateTruckPosition();
  }

  fullRedraw() {
    // Recalculate options like scroll positions, sizes, etc.
    this.recalculateOptions();

    // Reset SVG element
    this.svg.innerHTML = '';

    // Redraw
    this.setConnectorSpacerHeights();
    this.path = this.drawConnections(this.svg);
    this.drawTruck(this.svg);
    this.updateTruckPosition();
  }

  drawTruck(svgElement) {
    this.truckGroup = truckGroupNode();

    svgElement.appendChild(this.truckGroup);
  }

  updateTruckPosition() {
    const scrollFraction = this.scrollY / (this.startY + this.endY);

    let isInvisible = false;

    if (scrollFraction <= 0) {
      isInvisible = true;
    } else if (scrollFraction >= 1) {
      isInvisible = true;
    }

    this.truckGroup.setAttribute('opacity', isInvisible ? 0 : 1);

    const pathLength = this.path.getTotalLength();

    const pointAtLength = scrollFraction * pathLength;
    const point = this.path.getPointAtLength(pointAtLength);

    const nextPoint = this.path.getPointAtLength(pointAtLength + 1);
    const angle = Math.atan2(nextPoint.y - point.y, nextPoint.x - point.x) * (180 / Math.PI);

    const direction = this.previousScrollY < this.scrollY ? 'down' : 'up';

    // The angle above would drive the truck sideways, so we've to add some extra degrees
    // to get the truck in the right direction.
    const extraAngle = direction === 'down' ? 90 : -90;

    this.truckGroup.setAttribute('transform', `translate(${point.x},${point.y}) rotate(${angle - extraAngle})`);
  }

  drawConnections(svgElement) {
    const blocks = this.blockTargets;

    // Create <path> nodes.
    // The first path is the full line through the blocks, this is used as guiding
    // path for the 'car/truck'.
    // The other paths draw only parts of the line, based on the color.
    const path = pathNode(1);
    const lightGreyPath = pathNode(this.strokeWidth, '#f6f6f6');
    const lightBluePath = pathNode(this.strokeWidth, '#dfe6f2');
    const bluePath = pathNode(this.strokeWidth, '#1959c0');

    let generalPathData = '';
    let lightGreyPathData = '';
    let lightBluePathData = '';
    let bluePathData = '';

    for (let i = 0; i < blocks.length; i += 1) {
      const block = blocks[i];
      const {
        connectorTop,
        connectorBottom,
        connectorShape,
        connectorColor,
      } = block.dataset;

      const topX = this.isMobile
        ? this.strokeWidth / 2
        : this.getBlockTopXOffset(connectorTop);
      const bottomX = this.isMobile
        ? this.strokeWidth / 2
        : this.getBlockBottomXOffset(connectorBottom);

      const topY = block.offsetTop;
      const bottomY = topY + block.offsetHeight;

      const pathData = this.createConnectorShape(connectorShape, topX, topY, bottomX, bottomY);

      generalPathData += pathData;

      if (connectorColor === 'light-grey') {
        lightGreyPathData += pathData;
      }

      if (connectorColor === 'light-blue') {
        lightBluePathData += pathData;
      }

      if (connectorColor === 'blue') {
        bluePathData += pathData;
      }
    }

    path.setAttribute('d', generalPathData);
    lightGreyPath.setAttribute('d', lightGreyPathData);
    lightBluePath.setAttribute('d', lightBluePathData);
    bluePath.setAttribute('d', bluePathData);

    svgElement.appendChild(path);
    svgElement.appendChild(lightGreyPath);
    svgElement.appendChild(lightBluePath);
    svgElement.appendChild(bluePath);

    return path;
  }

  createConnectorShape(connectorShape, topX, topY, bottomX, bottomY) {
    if (this.isMobile) {
      return `M ${topX} ${topY} L ${bottomX} ${bottomY}`;
    }

    // Line coming in from the top, going horizontally, and down the bottom
    if (connectorShape === 'horizontal-line') {
      return (new ConnectorHorizontal(
        topX, topY, bottomX, bottomY,
      )).draw();
    }

    if (connectorShape === 'review-connector') {
      return (new ConnectorReview(
        topX, topY, bottomX, bottomY,
      )).draw();
    }

    if (connectorShape === 'process-connector') {
      return (new ConnectorProcess(
        topX, topY, bottomX, bottomY,
      )).draw();
    }

    if (connectorShape === 'socials-connector') {
      return (new ConnectorSocials(
        topX, topY, bottomX, bottomY,
      )).draw();
    }

    if (connectorShape === 'apply-connector') {
      return (new ConnectorApply(
        topX, topY, bottomX, bottomY,
      )).draw();
    }

    // Default is a simple line from the start to the end
    return ` M ${topX} ${topY} L ${bottomX} ${bottomY}`;
  }

  getBlockTopXOffset(connectorTop) {
    const topXPercentage = match(connectorTop, {
      '0/0': 0,
      '1/3': 33,
      '2/3': 66,
      '1/1': 100,
    });

    const elementWidth = this.element.offsetWidth - this.strokeWidth;

    const topX = ((topXPercentage / 100) * elementWidth) + (this.strokeWidth / 2);

    return topX;
  }

  getBlockBottomXOffset(connectorBottom) {
    const bottomXPercentage = match(connectorBottom, {
      '0/0': 0,
      '1/3': 33,
      '2/3': 66,
      '1/1': 100,
    });

    const elementWidth = this.element.offsetWidth - this.strokeWidth;

    const bottomX = ((bottomXPercentage / 100) * elementWidth) + (this.strokeWidth / 2);

    return bottomX;
  }
}

Connector.targets = [
  'block',
];

export default Connector;
